import React, { useState, useEffect } from 'react';
import { supabase } from '../services/supabaseService';
import { ai, AI_PROXY_CHAT_MODEL } from '../services/geminiService';
import { MessageSquare, Plus, Trash2, Edit2, Check, Loader2, Wand2, ArrowLeft } from 'lucide-react';

interface WhatsAppTemplate {
    id: string;
    name: string;
    body: string;
    pipelineStatus?: string;
    pipelineStatuses?: string[];
    creatorId?: string;
    visibility?: 'company' | 'private' | 'specific';
    sharedWithIds?: string[];
}

interface WhatsAppTemplatesSettingsTabProps {
    clientId: string;
}

export const WhatsAppTemplatesSettingsTab: React.FC<WhatsAppTemplatesSettingsTabProps> = ({ clientId }) => {
    const [templates, setTemplates] = useState<WhatsAppTemplate[]>([]);
    const [isLoading, setIsLoading] = useState(true);
    const [isSaving, setIsSaving] = useState(false);
    const [isGenerating, setIsGenerating] = useState(false);
    const [teamMembers, setTeamMembers] = useState<{ id: string, name: string }[]>([]);
    const [currentUserId, setCurrentUserId] = useState<string>('');

    const [editingTemplate, setEditingTemplate] = useState<WhatsAppTemplate | null>(null);
    const [aiPrompt, setAiPrompt] = useState('');

    useEffect(() => {
        loadTemplates();
        fetchTeamMembers();
        supabase.auth.getUser().then(({ data }) => { if (data.user) setCurrentUserId(data.user.id); });
    }, [clientId]);

    const fetchTeamMembers = async () => {
        try {
            const { data } = await supabase.from('profiles').select('id, name').eq('client_id', clientId);
            if (data) setTeamMembers(data);
        } catch(e) {}
    };

    const loadTemplates = async () => {
        setIsLoading(true);
        try {
            const { data, error } = await supabase.from('clients').select('data').eq('id', clientId).single();
            if (error) throw error;
            if (data?.data?.whatsapp_templates) {
                setTemplates(data.data.whatsapp_templates);
            }
        } catch (e) {
            console.error("Fout bij laden whatsapp templates", e);
        } finally {
            setIsLoading(false);
        }
    };

    const saveTemplatesToDb = async (newTemplates: WhatsAppTemplate[]) => {
        setIsSaving(true);
        try {
            const { data: clientData } = await supabase.from('clients').select('data').eq('id', clientId).single();
            const currentData = clientData?.data || {};
            currentData.whatsapp_templates = newTemplates;

            const { error } = await supabase.from('clients').update({ data: currentData }).eq('id', clientId);
            if (error) throw error;
            setTemplates(newTemplates);
            setEditingTemplate(null);
        } catch (e) {
            console.error("Fout bij opslaan", e);
            alert("Kon templates niet opslaan.");
        } finally {
            setIsSaving(false);
        }
    };

    const handleCreateNew = () => {
        setAiPrompt('');
        setEditingTemplate({
            id: `wa_tpl_${Date.now()}`,
            creatorId: currentUserId,
            visibility: 'company',
            sharedWithIds: [],
            name: 'Nieuwe WhatsApp Template',
            body: '',
            pipelineStatuses: []
        });
    };

    const handleDelete = async (id: string) => {
        if (!confirm("Weet je zeker dat je deze template wilt verwijderen?")) return;
        const newTemplates = templates.filter(t => t.id !== id);
        await saveTemplatesToDb(newTemplates);
    };

    const handleGenerateAI = async () => {
        if (!editingTemplate) return;
        const topic = aiPrompt.trim();
        if (!topic) return alert("Vul eerst in waar het appje over moet gaan.");

        setIsGenerating(true);
        try {
            const promptStr = `Je bent Froukje, een vlotte recruiter. Schrijf een WhatsApp template voor het onderwerp: "${topic}". 
            
Lever ALTIJD een JSON string op met deze keys:
{"body": "De inhoud van het WhatsApp bericht."}

Regels:
- Kort en bondig (max 3-4 zinnen).
- Gebruik eventueel WhatsApp styling (*vet*, _cursief_) en een paar emojis.
- Gebruik alleen de variabele [Voornaam] en verder GEEN andere placeholders. Verzin iets logisch.`;

            const response = await ai.models.generateContent({
                model: AI_PROXY_CHAT_MODEL,
                contents: promptStr,
                config: { responseMimeType: "application/json" }
            });

            if (response.text) {
                const parsed = JSON.parse(response.text);
                setEditingTemplate({
                    ...editingTemplate,
                    body: parsed.body || ''
                });
            }
        } catch (e) {
            console.error("Generatie mislukt", e);
            alert("Genereren mislukt. Probeer het opnieuw.");
        } finally {
            setIsGenerating(false);
        }
    };

    const handleSaveTemplate = async () => {
        if (!editingTemplate) return;
        if (!editingTemplate.name.trim()) return alert("Vul een naam in voor de template.");

        const isExisting = templates.some(t => t.id === editingTemplate.id);
        const newTemplates = isExisting
            ? templates.map(t => t.id === editingTemplate.id ? editingTemplate : t)
            : [...templates, editingTemplate];

        await saveTemplatesToDb(newTemplates);
    };

    if (isLoading) {
        return <div className="p-10 flex justify-center text-slate-400"><Loader2 className="animate-spin" /></div>;
    }

    if (editingTemplate) {
        return (
            <div className="max-w-3xl space-y-6">
                <div className="flex items-center justify-between">
                    <div>
                        <button onClick={() => { setEditingTemplate(null); setAiPrompt(''); }} className="flex items-center gap-2 text-[10px] font-black uppercase tracking-widest text-slate-500 hover:text-slate-900 mb-2">
                            <ArrowLeft size={14} /> Terug naar overzicht
                        </button>
                        <h2 className="text-xl font-black text-slate-900 uppercase tracking-tight flex items-center gap-3">
                            <MessageSquare className="text-[#25D366]" size={20} /> App Template Bewerken
                        </h2>
                    </div>
                </div>

                <div className="bg-white p-6 rounded-2xl border border-slate-200 shadow-sm space-y-4">
                    <div>
                        <label className="text-[10px] font-black uppercase tracking-widest text-slate-500 block mb-1.5">Template Naam</label>
                        <input
                            type="text"
                            value={editingTemplate.name}
                            onChange={(e) => setEditingTemplate({ ...editingTemplate, name: e.target.value })}
                            placeholder="Bijv. Uitnodiging Bellen"
                            className="w-full p-3 bg-slate-50 border border-slate-200 rounded-xl text-sm font-bold text-slate-900 outline-none focus:border-[#25D366]"
                        />
                    </div>

                    <div>
                        <label className="text-[10px] font-black uppercase tracking-widest text-slate-500 block mb-1.5">Gekoppelde Pipeline Statussen (Optioneel)</label>
                        <div className="flex flex-wrap gap-2">
                            {['Shortlist', 'Voorgesteld', 'Gesprek', 'Aanbod', 'Plaatsing', 'Rejected'].map(status => {
                                const currentStatuses = editingTemplate.pipelineStatuses || (editingTemplate.pipelineStatus ? [editingTemplate.pipelineStatus] : []);
                                const isSelected = currentStatuses.includes(status);
                                return (
                                    <label key={status} className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg border text-xs font-bold cursor-pointer transition-colors ${isSelected ? 'bg-emerald-50 border-emerald-200 text-emerald-700' : 'bg-white border-slate-200 text-slate-600 hover:bg-slate-50'}`}>
                                        <input 
                                            type="checkbox"
                                            className="hidden"
                                            checked={isSelected}
                                            onChange={(e) => {
                                                const newStatuses = e.target.checked ? [...currentStatuses, status] : currentStatuses.filter(s => s !== status);
                                                setEditingTemplate({ ...editingTemplate, pipelineStatuses: newStatuses });
                                            }}
                                        />
                                        {status === 'Rejected' ? 'Afgewezen' : status}
                                    </label>
                                );
                            })}
                        </div>
                        <p className="text-[10px] text-slate-400 font-medium mt-1.5">Koppel deze template aan een of meerdere fases, zodat hij sneller bovenaan staat in de mail-composer.</p>
                    </div>

                    <div className="pt-4 border-t border-slate-100 flex flex-col gap-3">
                        <label className="text-[10px] font-black uppercase tracking-widest text-slate-500 block">AI Assistent (Optioneel)</label>
                        <div className="flex gap-2">
                            <input
                                type="text"
                                value={aiPrompt}
                                onChange={e => setAiPrompt(e.target.value)}
                                placeholder="Waar moet dit appje over gaan? (bijv. 'Korte kennismaking plannen')"
                                className="flex-1 p-3 bg-emerald-50/50 border border-emerald-100 rounded-xl text-sm font-medium text-slate-900 outline-none focus:border-emerald-300 placeholder:text-emerald-300"
                            />
                            <button
                                onClick={handleGenerateAI}
                                disabled={isGenerating || !aiPrompt.trim()}
                                className="px-4 py-2 bg-emerald-100 text-emerald-700 rounded-xl text-xs font-black uppercase tracking-widest hover:bg-emerald-200 flex items-center gap-2 disabled:opacity-50 transition-colors shrink-0"
                            >
                                {isGenerating ? <Loader2 size={14} className="animate-spin" /> : <Wand2 size={14} />} Genereer met AI
                            </button>
                        </div>
                    </div>

                    <div>
                        <div className="flex justify-between items-end mb-1.5 pt-4 border-t border-slate-100">
                            <label className="text-[10px] font-black uppercase tracking-widest text-slate-500 block">WhatsApp Bericht</label>
                            <span className="text-[9px] font-bold text-slate-400 flex flex-wrap items-center gap-1 justify-end">Gebruik variabele: <span className="text-emerald-600 bg-emerald-50 px-1 py-0.5 rounded">[Voornaam]</span> <span className="text-emerald-600 bg-emerald-50 px-1 py-0.5 rounded">[Achternaam]</span></span>
                        </div>
                        <textarea
                            value={editingTemplate.body}
                            onChange={(e) => setEditingTemplate({ ...editingTemplate, body: e.target.value })}
                            placeholder="Typ hier het WhatsApp bericht..."
                            className="w-full h-40 p-3 bg-white border border-slate-200 rounded-xl text-sm font-medium text-slate-900 outline-none focus:border-[#25D366] resize-none"
                        />
                    </div>

                    <button
                        onClick={handleSaveTemplate}
                        disabled={isSaving}
                        className="w-full py-3 bg-emerald-500 text-white rounded-xl text-xs font-black uppercase tracking-widest hover:bg-emerald-600 flex items-center justify-center gap-2 transition-colors"
                    >
                        {isSaving ? <Loader2 size={16} className="animate-spin" /> : <Check size={16} />}
                        Template Opslaan
                    </button>
                </div>
            </div>
        );
    }

    return (
        <div className="max-w-4xl space-y-6">
            <div className="flex items-center justify-between">
                <div>
                    <h2 className="text-xl font-black text-slate-900 uppercase tracking-tight flex items-center gap-3">
                        <MessageSquare className="text-[#25D366]" size={24} /> WhatsApp Templates
                    </h2>
                    <p className="text-xs text-slate-500 font-bold mt-1">Beheer herbruikbare appjes gestuurd via de Talentpool.</p>
                </div>
                <button
                    onClick={handleCreateNew}
                    className="flex items-center gap-2 bg-[#25D366] text-white px-5 py-2.5 rounded-xl font-black text-[10px] uppercase tracking-widest shadow-md hover:bg-[#1DA851] transition-all"
                >
                    <Plus size={16} /> Nieuwe Template
                </button>
            </div>

            <div className="pt-4 border-t border-slate-100">
                <div className="flex items-center justify-between mb-4">
                    <h3 className="font-bold text-slate-900 flex items-center gap-2">Mijn Templates ({templates.length})</h3>
                </div>

                {templates.length === 0 ? (
                    <div className="p-12 text-center bg-slate-50 rounded-2xl border border-slate-200 border-dashed">
                        <MessageSquare className="mx-auto text-slate-300 mb-3" size={32} />
                        <p className="text-sm font-bold text-slate-500">Nog geen WhatsApp templates ingesteld.</p>
                    </div>
                ) : (
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        {templates.map(tpl => (
                            <div key={tpl.id} className="bg-white p-5 rounded-2xl border border-slate-200 shadow-sm hover:border-[#25D366] transition-colors flex flex-col justify-between min-h-[160px]">
                                <div>
                                    <h4 className="text-sm font-black text-slate-900 mb-2">{tpl.name}</h4>
                                    {(() => {
                                        const statuses = tpl.pipelineStatuses || (tpl.pipelineStatus ? [tpl.pipelineStatus] : []);
                                        if (statuses.length === 0) return null;
                                        return (
                                            <div className="flex flex-wrap gap-1 mb-2">
                                                {statuses.map(s => (
                                                    <span key={s} className="inline-block px-1.5 py-0.5 bg-emerald-50 text-emerald-600 text-[8px] font-black uppercase tracking-widest rounded-md border border-emerald-100">
                                                        {s === 'Rejected' ? 'Afgewezen' : s}
                                                    </span>
                                                ))}
                                            </div>
                                        );
                                    })()}
                                    <p className="text-xs text-slate-600 line-clamp-4 leading-relaxed whitespace-pre-wrap">{tpl.body}</p>
                                </div>
                                <div className="flex gap-2 justify-end mt-4 pt-4 border-t border-slate-50">
                                    <button
                                        onClick={() => setEditingTemplate(tpl)}
                                        className="p-2 text-slate-400 hover:text-[#25D366] bg-slate-50 hover:bg-emerald-50 rounded-lg flex items-center gap-2 transition-colors"
                                    >
                                        <Edit2 size={14} /> <span className="text-[10px] font-black uppercase tracking-widest hidden sm:inline">Bewerk</span>
                                    </button>
                                    <button
                                        onClick={() => handleDelete(tpl.id)}
                                        className="p-2 text-slate-400 hover:text-red-500 bg-slate-50 hover:bg-red-50 rounded-lg transition-colors"
                                    >
                                        <Trash2 size={14} />
                                    </button>
                                </div>
                            </div>
                        ))}
                    </div>
                )}
            </div>
        </div>
    );
};
